export const milissegundos_por_dia = 1000 * 60 * 60 * 24;
export const diasDaSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/** Fuso usado ao exibir mês/ano de folhas (meio-dia local evita deslocar o mês por UTC). */
export const TIMEZONE_AMERICA_SAO_PAULO = 'America/Sao_Paulo';

const REGEX_ANO_MES = /^\d{4}-\d{2}$/;

/**
 * Formata ano e mês (1–12) como mês por extenso + ano em pt-BR, ex.: "Janeiro de 2025".
 */
export function formatarMesAnoLegivel(ano: number, mes: number): string {
  const date = new Date(`${ano}-${String(mes).padStart(2, '0')}-01T12:00:00`);
  const r = new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIMEZONE_AMERICA_SAO_PAULO,
    month: 'long',
    year: 'numeric',
  }).format(date);
  return r.charAt(0).toUpperCase() + r.slice(1);
}

/**
 * A partir da string "yyyy-mm", retorna mês por extenso + ano em pt-BR.
 * Retorna "—" se o formato ou a data forem inválidos.
 */
export function formatarYyyyMmMesExtensoOuIndefinido(ymd: string): string {
  if (!REGEX_ANO_MES.test(ymd)) {
    return '—';
  }
  const date = new Date(`${ymd}-01T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const r = new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIMEZONE_AMERICA_SAO_PAULO,
    month: 'long',
    year: 'numeric',
  }).format(date);
  return r.charAt(0).toUpperCase() + r.slice(1);
}

/**
 * Deixa uma data amigável para leitura.
 * @param {Date} original
 * @returns string
 */
export function DataAmigavel(original?: Date | string | null) {
  if (original) {
    return new Date(original).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }
  return '';
}

/**
 * Periodos aquisitivos no formato ano/ano
 * @param data1 Data.
 * @returns
 */
export function PeriodosAquisitos(data1: Date) {
  const d = new Date(data1);
  return (
    DataAmigavel(new Date(d.setFullYear(d.getFullYear() - 1))).slice(6) +
    DataAmigavel(data1).slice(5)
  );
}

export function DataEHoraAmigavel(original?: Date | string) {
  if (original) {
    return new Date(Date.parse(original as string)).toLocaleString('pt-br');
  }
  return '';
}

/**
 * Converte um data para o padrão ISO-8601 (usado em banco e campos de data de formulário).
 * YYY-MM-DD “T” hh:mm:ss.SSSZ
 * @param {string | Date} input
 * @param [formato='yyyy-MM-dd']
 * @returns Date
 */
export function DataIso(input?: string | Date, formato = 'yyyy-MM-dd') {
  if (typeof input === 'undefined' || input == null) return '';

  const regexDate = /^\d{4}-\d{2}-\d{2}$/;
  const regexYearMonth = /^\d{4}-\d{2}$/;
  const regexDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

  let y, m, d;
  if (input instanceof Date) {
    y = input.getFullYear();
    m = String(input.getMonth() + 1).padStart(2, '0');
    d = String(input.getDate()).padStart(2, '0');
  } else if (typeof input === 'string') {
    const [year, month, day] = input.split('-');

    if (regexDate.test(input)) ((y = year), (m = month), (d = day));
    else if (regexYearMonth.test(input))
      ((y = year), (m = String(month).padStart(2, '0')), (d = '01'));
    else if (regexDateTime.test(input)) {
      const [ds] = day.split('T');
      ((y = year), (m = month.padStart(2, '0')), (d = ds.padStart(2, '0')));
    } else return input;
  }

  return formato === 'yyyy-MM-dd' ? `${y}-${m}-${d}` : `${y}-${m}`;
}

/**
 * Retorna mês e ano de uma data.
 * @param original Data original.
 * @returns "yyyy-mm"
 */
export function AnoEMesIso(original: Date) {
  if (typeof original === 'undefined' || original == null) return '';
  const y = original.getFullYear();
  let m = (original.getMonth() + 1).toString();
  if (m.length <= 1) m = '0' + m;
  return y + '-' + m;
}

/**
 * Diferença entre duas datas expressa em dias.
 * @param inicio Data de início.
 * @param fim Data de fim.
 * @returns Dias entre uma data e outra.
 */
export function DiasEntreDuasDatas(
  inicio?: Date | string | null,
  fim?: Date | string | null,
  inclusive: boolean = true,
) {
  if (inicio && fim) {
    return (
      (new Date(fim).getTime() - new Date(inicio).getTime()) /
      milissegundos_por_dia +
      (inclusive ? 1 : 0)
    );
  }
  return '';
}

/**
 * Calcula os anos de uma data até hoje.
 * @param data Data de admissão.
 * @returns Texto amigável para interface.
 */
export function TempoAmigavelAteHoje(data: Date) {
  const hoje = new Date(Date.now());
  const admissao = new Date(data);

  const utc1 = Date.UTC(
    admissao.getFullYear(),
    admissao.getMonth(),
    admissao.getDate(),
  );
  const utc2 = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

  const dias = Math.floor((utc2 - utc1) / milissegundos_por_dia);

  if (dias <= 31) return Math.floor(dias) + ' dias';
  else if (dias > 31 && dias < 365) {
    return Math.floor(dias / 30) + ' meses';
  } else if (dias > 365) return Math.floor(dias / 365) + ' anos';
  return '';
}

/**
 * Normaliza uma data.
 * @param data Data.
 * @returns Data normalizada.
 */
export function NormalizarData(data: Date | string) {
  return new Date(
    new Date(data).toLocaleDateString('en-us', { timeZone: 'UTC' }),
  );
}

/**
 * Converte um data incompleta para data iso com timezone -3.
 * @param dateInput Data de entrada.
 * @returns Data no formato yyyy-mm-ddT03:00:00.000Z
 */
export function convertToGMTPlus3(dateInput: string | Date): Date {
  if (dateInput instanceof Date)
    return dateInput;

  if (typeof dateInput !== 'string')
    throw new Error(`Entrada de data inválida: ${dateInput}`);

  if (dateInput.length > 10)
    return new Date(dateInput);

  // Se a data veio no formato yyyy-mm, adiciona o dia 01
  if (dateInput.length === 7)
    dateInput = dateInput.concat('-01');

  const date = new Date(`${dateInput}T00:00:00Z`);

  if (isNaN(date.getTime()))
    throw new Error(`Formato de data inválido: ${date}`);

  const offset = +3 * 60;
  date.setMinutes(date.getMinutes() + offset);

  return date;
}

/**
 * Returns the name of the day of the week in Portuguese for a given date.
 *
 * @param data - The date for which to get the day of the week. Defaults to the current date if not provided.
 * @returns The name of the day of the week in Portuguese (e.g., 'domingo', 'segunda-feira').
 */
export function dayOfTheWeek(data = new Date()) {
  const dias = [
    'Domingo',
    'Segunda-Feira',
    'Terça-Feira',
    'Quarta-Feira',
    'Quinta-Feira',
    'Sexta-Feira',
    'Sábado',
  ];
  return dias[data.getDay()];
}


export const normalizeMonthParam = (value: string): Date | null => {
  // Aceita "YYYY-MM-DD" ou "YYYY-MM" e interpreta como data LOCAL.
  // Evita new Date("YYYY-MM-DD") (UTC) que pode "voltar" um dia/mês em fusos negativos.
  const v = value.length === 7 ? `${value}-01` : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  const dt = new Date(year, month - 1, 1);
  if (isNaN(dt.getTime())) return null;
  return dt;
};

export const formatMonthParam = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};